import { useEffect, useRef, useState } from "react"
import { Alert, Box, Button, Stack, Typography } from "@mui/material"
import { Modal } from "@core/components/modal"
import type { MessageContent } from "@core/components/telegram/types"
import type { TelegramPreviewMedia } from "@core/components/telegram/preview/telegramMessagePreview"
import type { TelegramMessageEditorProps } from "@core/components/telegram/editor/telegramMessageEditor"
import {
  useMediaUploads,
  type MediaWriteAccess,
} from "../../hooks/useMediaUploads"
import { mediaDescriptor, useMediaPreview } from "../../hooks/useMediaPreview"
import { usePostBotSession } from "../../hooks/usePostBotSession"
import { useMediaVisibility } from "../../hooks/useMediaVisibility"
import type { Media } from "../../interface/media"
import { MediaLibrary } from "./mediaLibrary"
import { MediaThumbnail } from "./mediaThumbnail"
import { MediaUploadQueue } from "./mediaUploadQueue"

type Preview = NonNullable<TelegramMessageEditorProps["media"]>[number]
const move = (ids: string[], from: number, to: number) => {
  const next = [...ids]
  if (
    Number.isInteger(from) &&
    Number.isInteger(to) &&
    from >= 0 &&
    to >= 0 &&
    from < next.length &&
    to < next.length
  )
    next.splice(to, 0, next.splice(from, 1)[0])
  return next
}

export interface MediaPickerProps extends MediaWriteAccess {
  mediaIds: string[]
  onChange: (ids: string[]) => void
  media?: Preview[]
  onMediaLoaded?: (media: Media[]) => void
  listKey?: string
  visible?: boolean
}

/** A picker is owned by one document/session. Only IDs cross its write boundary;
 * deleting a card detaches it, never deletes the library resource. */
export function MediaPicker(props: MediaPickerProps) {
  const session = usePostBotSession()
  return <PickerSession key={`${session}:${props.draftKey ?? ""}`} {...props} />
}

function PickerSession({
  mediaIds,
  onChange,
  media = [],
  onMediaLoaded,
  listKey,
  readOnly,
  canWrite,
  draftKey,
  visible = true,
}: MediaPickerProps) {
  const uploads = useMediaUploads(
    { readOnly, canWrite, draftKey },
    { retainCompletions: true },
  )
  const viewport = useMediaVisibility()
  const [known, setKnown] = useState<Record<string, TelegramPreviewMedia>>({})
  const [libraryOpen, setLibraryOpen] = useState(false)
  const [limitError, setLimitError] = useState(false)
  const consumed = useRef(new Set<string>())
  const disabled = readOnly || canWrite === false
  const selection = useRef(mediaIds)
  const previousIds = useRef(mediaIds)
  if (previousIds.current !== mediaIds) {
    selection.current = mediaIds
    previousIds.current = mediaIds
  }
  const remember = (values: Media[]) => {
    setKnown((previous) => ({
      ...previous,
      ...Object.fromEntries(
        values.map((value) => [value.id, mediaDescriptor(value)]),
      ),
    }))
    onMediaLoaded?.(values)
  }
  const change = (ids: string[]) => {
    selection.current = ids
    onChange(ids)
  }
  // Completion order must not become selection order. An earlier successful
  // retry is inserted before its already attached later sibling. Never reattach
  // a completed card that the user subsequently detached or reordered.
  useEffect(() => {
    const live = new Set(uploads.items.map((item) => item.localId))
    for (const id of consumed.current)
      if (!live.has(id)) consumed.current.delete(id)
    if (disabled) {
      // Access loss pauses unconsumed completions, not cleanup of previews that
      // were already handed off and have since been detached by the caller.
      uploads.retainCompleted(selection.current, consumed.current)
      return
    }
    let ids = [...selection.current]
    const completed: Media[] = []
    uploads.items.forEach((item, index) => {
      if (item.status !== "success" || consumed.current.has(item.localId))
        return
      consumed.current.add(item.localId)
      if (item.media?.status !== "ready") return
      completed.push(item.media)
      if (ids.includes(item.media.id)) return
      if (ids.length >= 10) {
        setLimitError(true)
        return
      }
      const following = uploads.items
        .slice(index + 1)
        .find((next) => next.media && ids.includes(next.media.id))?.media?.id
      ids.splice(
        following ? ids.indexOf(following) : ids.length,
        0,
        item.media.id,
      )
    })
    if (completed.length) {
      remember(completed)
      if (ids.join() !== selection.current.join()) change(ids)
    }
    uploads.retainCompleted(selection.current, consumed.current)
  })
  const previews = useMediaPreview(
    mediaIds,
    [
      ...Object.values(known),
      ...media,
      ...uploads.items.flatMap((item) =>
        item.media
          ? [{ ...mediaDescriptor(item.media), previewUrl: item.previewUrl }]
          : [],
      ),
    ],
    visible && viewport.visible,
  )
  // Completed uploads may remain mounted after an editor-side detach. They
  // cannot auto-attach again and must not reserve capacity for future choices.
  const pendingCount = uploads.items.filter(
    (item) =>
      !consumed.current.has(item.localId) &&
      (!item.media || !mediaIds.includes(item.media.id)),
  ).length
  const add = (value: Media) => {
    if (disabled || value.status !== "ready" || mediaIds.includes(value.id))
      return
    if (mediaIds.length + pendingCount >= 10) {
      setLimitError(true)
      return
    }
    remember([value])
    change([...mediaIds, value.id])
  }
  return (
    <Stack spacing={2} ref={viewport.ref}>
      <MediaUploadQueue
        uploads={{
          ...uploads,
          addFiles: (files) => {
            const available = Math.max(0, 10 - mediaIds.length - pendingCount)
            setLimitError(files.length > available)
            uploads.addFiles(files.slice(0, available))
          },
        }}
        readOnly={readOnly}
        canWrite={canWrite}
      />
      <Button
        disabled={disabled}
        onClick={() => setLibraryOpen((open) => !open)}
      >
        Выбрать из библиотеки
      </Button>
      {limitError && (
        <Alert severity="warning">Не больше 10 вложений в сообщении.</Alert>
      )}
      <Stack
        component="ol"
        aria-label="Выбранные медиа"
        spacing={1}
        sx={{ p: 0, listStyle: "none" }}
      >
        {previews.map((value, index) => (
          <Stack component="li" key={value.id} spacing={1}>
            <Typography sx={{ overflowWrap: "anywhere" }}>
              {value.name}
            </Typography>
            <Box sx={{ maxWidth: 240 }}>
              <MediaThumbnail media={value} />
            </Box>
            <Stack direction="row" sx={{ flexWrap: "wrap" }}>
              <Button
                aria-label={`Медиа ${index + 1} вверх`}
                disabled={disabled || index === 0}
                onClick={() => change(move(mediaIds, index, index - 1))}
              >
                ↑
              </Button>
              <Button
                aria-label={`Медиа ${index + 1} вниз`}
                disabled={disabled || index === mediaIds.length - 1}
                onClick={() => change(move(mediaIds, index, index + 1))}
              >
                ↓
              </Button>
              <Button
                aria-label={`Убрать из сообщения медиа ${index + 1}`}
                disabled={disabled}
                onClick={() => {
                  change(mediaIds.filter((id) => id !== value.id))
                  uploads.items
                    .filter((item) => item.media?.id === value.id)
                    .forEach((item) => uploads.remove(item.localId))
                }}
              >
                Убрать из сообщения
              </Button>
            </Stack>
          </Stack>
        ))}
      </Stack>
      {libraryOpen && visible && (
        <MediaLibrary
          readOnly={readOnly}
          canWrite={canWrite}
          selectedIds={mediaIds}
          onSelect={add}
          listKey={listKey}
        />
      )}
    </Stack>
  )
}

export interface MediaEditorAdapterProps extends MediaWriteAccess {
  value: MessageContent
  onChange: (value: MessageContent) => void
  media?: Preview[]
  listKey?: string
}

/** Core only receives render descriptors and ID callbacks. Uploads update the
 * current draft immediately; album conflicts retain both IDs and keyboard. */
export function useMediaEditorAdapter({
  value,
  onChange,
  media = [],
  listKey,
  readOnly,
  canWrite,
  draftKey,
}: MediaEditorAdapterProps) {
  const [open, setOpen] = useState(false)
  const [opened, setOpened] = useState(false)
  const [known, setKnown] = useState<Record<string, TelegramPreviewMedia>>({})
  const disabled = readOnly || canWrite === false
  const viewport = useMediaVisibility()
  const descriptors = useMediaPreview(
    value.mediaIds,
    [...Object.values(known), ...media],
    viewport.visible,
  )
  return {
    previewRef: viewport.ref,
    editorProps: {
      readOnly: disabled,
      keyboardAllowed: value.mediaIds.length < 2,
      media: descriptors,
      onAddMedia: () => {
        if (!disabled) {
          setOpened(true)
          setOpen(true)
        }
      },
      onRemoveMedia: (id: string) => {
        if (!disabled)
          onChange({
            ...value,
            mediaIds: value.mediaIds.filter((value) => value !== id),
          })
      },
      onMoveMedia: (from: number, to: number) => {
        if (!disabled)
          onChange({ ...value, mediaIds: move(value.mediaIds, from, to) })
      },
    },
    picker: opened ? (
      <Modal
        open={open}
        title="Вложения сообщения"
        dialogProps={{ maxWidth: "md", keepMounted: true }}
        onClose={() => setOpen(false)}
        actions={[
          <Button key="close" onClick={() => setOpen(false)}>
            Готово
          </Button>,
        ]}
      >
        <MediaPicker
          mediaIds={value.mediaIds}
          onChange={(ids) => {
            if (!disabled) onChange({ ...value, mediaIds: ids })
          }}
          media={[...Object.values(known), ...media]}
          onMediaLoaded={(values) =>
            setKnown((previous) => ({
              ...previous,
              ...Object.fromEntries(
                values.map((value) => [value.id, mediaDescriptor(value)]),
              ),
            }))
          }
          listKey={listKey}
          draftKey={draftKey}
          readOnly={disabled}
          visible={open}
        />
      </Modal>
    ) : null,
  }
}
